import { isNumberNode, Node, Plugin } from '@sagold/react-json-editor';

import { CustomNode, getNodeFromJsonPath } from './pluginHelper';
import { getCustomNode, isChangeEvent } from './pluginHelper';

function getNodeValue(
  currentJsonPath: string,
  toFindJsonPath: string | number,
  rootNode: Node
) {
  let value: number;
  if (typeof toFindJsonPath === 'number') {
    value = toFindJsonPath;
  } else {
    const leftNode = getNodeFromJsonPath(
      currentJsonPath,
      toFindJsonPath,
      rootNode
    );
    value = isNumberNode(leftNode) ? (leftNode.value ?? 0) : 0;
  }
  return value;
}

function calculate(i: number, type: string, j: number): number | null {
  if (typeof i !== 'number' || typeof j !== 'number') {
    return null;
  }
  switch (type) {
    case '*':
      return i * j;
    case '+':
      return i + j;
    case '-':
      return i - j;
    case '/':
      if (j !== 0) {
        try {
          return Math.round(i / j);
        } catch (e) {
          console.error(e);
        }
      }
      return null;
    default:
      return null;
  }
}

function performCalculation(rootNode: Node, updateValueNode: CustomNode) {
  const updateValues = updateValueNode.schema['updateValue'] as (
    | string
    | number
  )[];

  if (updateValues == null || updateValues.length < 3) {
    return null;
  }

  // perform basic math
  let j = 1,
    k = 2;
  let leftValue: number | null = getNodeValue(
    updateValueNode.pointer,
    updateValues[0],
    rootNode
  );
  while (k < updateValues.length) {
    const rightValue = getNodeValue(
      updateValueNode.pointer,
      updateValues[k],
      rootNode
    );
    if (leftValue) {
      leftValue = calculate(leftValue, String(updateValues[j]), rightValue);
    }
    if (leftValue == null) {
      k = updateValues.length; // break loop
    }
    j += 2;
    k += 2;
  }
  return leftValue;
}

function updateValue(rootNode: Node, updateValueNode: CustomNode) {
  const totalValue = performCalculation(rootNode, updateValueNode);
  const fractionDigits =
    typeof updateValueNode.schema.options?.digits === 'number'
      ? updateValueNode.schema.options?.digits
      : 2;
  if (totalValue != null) {
    updateValueNode.value = Number.parseFloat(
      totalValue.toFixed(fractionDigits)
    );
  }
}

/**
 * This plugin directly modifies the schema to work-around the batched updates performed by react-json-editor.
 */
export const BasicMathPlugin: Plugin = () => {
  return {
    id: 'basic-math-plugin',
    onEvent(root, event) {
      if (!isChangeEvent(event)) {
        return undefined;
      }
      for (const sum of getCustomNode(root, 'updateValue')) {
        updateValue(root, sum);
      }
      return [root, []];
    },
  };
};
